import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DEMO_SERVICES, CATEGORY_LABELS } from './demoServices.js';

const VIEWS = { HOME: 'home', DETAIL: 'detail', REQUEST: 'request', CONFIRM: 'confirm', GROCERY: 'grocery', GUIDEBOOK: 'guidebook', CHAT: 'chat' };
const CARDS_PER_PAGE = 4;
const AUTO_ADVANCE_MS = 4000;
const PAUSE_AFTER_INTERACT_MS = 8000;

const DS = {
  bg: '#ffffff',
  card: '#F7F7F8',
  cardBorder: '#EAEAEA',
  textPrimary: '#111111',
  textSecondary: '#5F6368',
  textMuted: '#888888',
  accent: '#EF4949',
  btnPrimary: '#111111',
  success: '#52c41a',
  divider: 'rgba(0,0,0,0.06)',
  radius: { sm: 8, md: 12, lg: 16, xl: 20, card: 24 },
  spacing: { xs: 4, sm: 8, md: 16, lg: 24, xl: 32 },
};

const PHONE_W = 268;
const PHONE_H = 554;

const base = {
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  WebkitFontSmoothing: 'antialiased',
  boxSizing: 'border-box',
};

const UNSPLASH_GUIDEBOOK_COVER = 'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=600&h=400&fit=crop&q=80';

/* ─── Mock Grocery Data ─── */
const GROCERY_CATEGORIES = ['All', 'Fruit', 'Dairy', 'Snacks', 'Coffee', 'Drinks', 'Bakery', 'Meat'];

const MOCK_PRODUCTS = [
  { id: 'p1', name: 'Organic Strawberries', price: '5.99', size: '16 oz', category: 'Fruit', image: 'https://images.unsplash.com/photo-1464965911861-746a04b4bca6?w=200&q=80' },
  { id: 'p2', name: 'Bananas', price: '0.79', size: 'per lb', category: 'Fruit', image: 'https://images.unsplash.com/photo-1571771894821-ce9b6c11b08e?w=200&q=80' },
  { id: 'p3', name: 'Hass Avocados', price: '1.99', size: 'each', category: 'Fruit', image: 'https://images.unsplash.com/photo-1523049673857-eb18f1d7b578?w=200&q=80' },
  { id: 'p4', name: 'Fresh Blueberries', price: '4.49', size: '6 oz', category: 'Fruit', image: 'https://images.unsplash.com/photo-1498557850523-fd3d118b962e?w=200&q=80' },
  { id: 'p5', name: 'Whole Milk', price: '4.29', size: '1 gal', category: 'Dairy', image: 'https://images.unsplash.com/photo-1563636619-e9143da7973b?w=200&q=80' },
  { id: 'p6', name: 'Greek Yogurt', price: '1.49', size: '5.3 oz', category: 'Dairy', image: 'https://images.unsplash.com/photo-1488477181946-6428a0291777?w=200&q=80' },
  { id: 'p7', name: 'Cheddar Cheese', price: '5.99', size: '8 oz', category: 'Dairy', image: 'https://images.unsplash.com/photo-1618164436241-4473940d1f5c?w=200&q=80' },
  { id: 'p8', name: 'Large Eggs', price: '4.99', size: 'dozen', category: 'Dairy', image: 'https://images.unsplash.com/photo-1582722872445-44dc5f7e3c8f?w=200&q=80' },
  { id: 'p9', name: 'Mixed Nuts', price: '8.99', size: '16 oz', category: 'Snacks', image: 'https://images.unsplash.com/photo-1599599810769-bcde5a160d32?w=200&q=80' },
  { id: 'p10', name: 'Granola Bars', price: '4.49', size: '6 ct', category: 'Snacks', image: 'https://images.unsplash.com/photo-1490885578174-acda8905c2c6?w=200&q=80' },
  { id: 'p11', name: 'Tortilla Chips', price: '3.99', size: '13 oz', category: 'Snacks', image: 'https://images.unsplash.com/photo-1566478989037-eec170784d0b?w=200&q=80' },
  { id: 'p12', name: 'Cold Brew Coffee', price: '5.49', size: '32 oz', category: 'Coffee', image: 'https://images.unsplash.com/photo-1461023058943-07fcbe16d735?w=200&q=80' },
  { id: 'p13', name: 'Coffee Beans', price: '9.99', size: '12 oz', category: 'Coffee', image: 'https://images.unsplash.com/photo-1559056199-641a0ac8b55e?w=200&q=80' },
  { id: 'p14', name: 'Sparkling Water', price: '5.99', size: '12 pk', category: 'Drinks', image: 'https://images.unsplash.com/photo-1558642452-9d2a7deb7f62?w=200&q=80' },
  { id: 'p15', name: 'Orange Juice', price: '4.99', size: '52 oz', category: 'Drinks', image: 'https://images.unsplash.com/photo-1621506289937-a8e4df240d0b?w=200&q=80' },
  { id: 'p16', name: 'Sourdough Bread', price: '4.49', size: 'loaf', category: 'Bakery', image: 'https://images.unsplash.com/photo-1509440159596-0249088772ff?w=200&q=80' },
  { id: 'p17', name: 'Butter Croissants', price: '3.99', size: '4 ct', category: 'Bakery', image: 'https://images.unsplash.com/photo-1530610476181-d83430b64dcd?w=200&q=80' },
  { id: 'p18', name: 'Chicken Breast', price: '8.49', size: 'per lb', category: 'Meat', image: 'https://images.unsplash.com/photo-1604503468506-a8da13d82791?w=200&q=80' },
];

/* ─── iOS Status Bar ─── */
function StatusBar({ light }) {
  const color = light ? '#fff' : DS.textPrimary;
  return (
    <div style={{
      ...base,
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 44,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 18px',
      zIndex: 5,
    }}>
      <span style={{ fontSize: 13, fontWeight: 600, color, lineHeight: 1 }}>9:41</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        {/* Signal bars */}
        <svg width="14" height="10" viewBox="0 0 14 10" fill="none">
          <rect x="0" y="7" width="2.5" height="3" rx="0.5" fill={color} />
          <rect x="3.5" y="5" width="2.5" height="5" rx="0.5" fill={color} />
          <rect x="7" y="2.5" width="2.5" height="7.5" rx="0.5" fill={color} />
          <rect x="10.5" y="0" width="2.5" height="10" rx="0.5" fill={color} />
        </svg>
        {/* WiFi */}
        <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
          <path d="M6 9a1 1 0 100-2 1 1 0 000 2z" fill={color} />
          <path d="M3.5 6.5a3.5 3.5 0 015 0" stroke={color} strokeWidth="1.2" strokeLinecap="round" fill="none" />
          <path d="M1.5 4.5a6 6 0 019 0" stroke={color} strokeWidth="1.2" strokeLinecap="round" fill="none" />
        </svg>
        {/* Battery */}
        <svg width="22" height="10" viewBox="0 0 22 10" fill="none">
          <rect x="0.5" y="0.5" width="18" height="9" rx="2" stroke={color} strokeWidth="1" fill="none" />
          <rect x="2" y="2" width="14" height="6" rx="1" fill={color} />
          <rect x="19.5" y="3" width="2" height="4" rx="0.5" fill={color} />
        </svg>
      </div>
    </div>
  );
}

/* ─── Service Card ─── */
function ServiceCardMini({ service, onTap }) {
  return (
    <button
      onClick={() => onTap(service)}
      aria-label={`View ${service.name} service`}
      style={{
        ...base,
        position: 'relative',
        width: '100%',
        aspectRatio: '1 / 1',
        borderRadius: DS.radius.lg,
        overflow: 'hidden',
        border: 'none',
        cursor: 'pointer',
        padding: 0,
        display: 'block',
        backgroundColor: '#2a2a2a',
      }}
    >
      <img
        src={service.image}
        alt=""
        loading="lazy"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(to bottom, transparent 20%, rgba(0,0,0,0.15) 50%, rgba(0,0,0,0.7) 100%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'flex-end',
          padding: 10,
        }}
      >
        <div
          style={{
            ...base,
            color: 'rgba(255,255,255,0.7)',
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: 2,
            textTransform: 'uppercase',
            marginBottom: 2,
          }}
        >
          {CATEGORY_LABELS[service.category] || service.category}
        </div>
        <div
          style={{
            ...base,
            color: '#fff',
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: -0.2,
            lineHeight: 1.2,
          }}
        >
          {service.name}
        </div>
      </div>
    </button>
  );
}

/* ─── Home View ─── */
function HomeView({ services, onSelectService, cardPage, setCardPage, onInteract, hostLogo, onOpenGuidebook, onOpenChat }) {
  const totalPages = Math.max(1, Math.ceil(services.length / CARDS_PER_PAGE));
  const safePage = Math.min(cardPage, totalPages - 1);

  return (
    <div style={{ ...base, width: PHONE_W, height: PHONE_H, background: DS.bg, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <StatusBar />

      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: `48px ${DS.spacing.lg}px 0` }}>
          <div
            style={{
              ...base,
              textAlign: 'center',
              marginBottom: DS.spacing.md,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              minHeight: hostLogo ? 120 : 40,
            }}
          >
            {hostLogo ? (
              <img
                src={hostLogo}
                alt="Host"
                style={{
                  display: 'block',
                  width: '100%',
                  height: 'auto',
                  maxWidth: '100%',
                  maxHeight: 120,
                  objectFit: 'contain',
                  objectPosition: 'center',
                }}
              />
            ) : (
              <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: 2, textTransform: 'uppercase', color: DS.textMuted }}>
                BELLHOPT
              </span>
            )}
          </div>

          <div style={{ ...base, fontSize: 26, fontWeight: 800, letterSpacing: -0.5, color: DS.textPrimary }}>
            Hello, John
          </div>

          <div style={{ display: 'flex', marginTop: DS.spacing.md }}>
            <div style={{ flex: 1 }}>
              <div style={{ ...base, fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: DS.textMuted, marginBottom: DS.spacing.xs }}>
                Check In
              </div>
              <div style={{ ...base, fontSize: 14, fontWeight: 600, color: DS.textPrimary }}>Jun 15, 2026</div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ ...base, fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: DS.textMuted, marginBottom: DS.spacing.xs }}>
                Checkout
              </div>
              <div style={{ ...base, fontSize: 14, fontWeight: 600, color: DS.textPrimary }}>Jun 20, 2026</div>
            </div>
          </div>

          <div style={{ height: 1, backgroundColor: DS.divider, margin: `${DS.spacing.md}px 0` }} />

          {/* AI Chat Input */}
          <button
            type="button"
            onClick={() => { onInteract(); onOpenChat(); }}
            aria-label="Open AI concierge chat"
            style={{
              ...base,
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              height: 38,
              borderRadius: 19,
              border: `1px solid ${DS.cardBorder}`,
              background: DS.card,
              padding: '0 6px 0 14px',
              marginBottom: 10,
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            <span style={{ flex: 1, fontSize: 12, fontWeight: 400, color: DS.textMuted }}>
              Ask your AI concierge…
            </span>
            <span
              style={{
                width: 26,
                height: 26,
                borderRadius: 13,
                background: DS.btnPrimary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2L2 12l10 10M2 12h20" transform="rotate(45 12 12)" />
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </span>
          </button>

          {/* Guidebook quick-access card */}
          <button
            type="button"
            onClick={() => { onInteract(); onOpenGuidebook(); }}
            aria-label="Open property guidebook"
            style={{
              ...base,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              width: '100%',
              padding: 6,
              borderRadius: 14,
              border: `1px solid ${DS.cardBorder}`,
              background: DS.bg,
              cursor: 'pointer',
              textAlign: 'left',
              marginBottom: 10,
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                overflow: 'hidden',
                flexShrink: 0,
                backgroundColor: '#2a2a2a',
                backgroundImage: `url(${UNSPLASH_GUIDEBOOK_COVER})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ ...base, fontSize: 12, fontWeight: 700, color: DS.textPrimary, lineHeight: 1.2 }}>
                Your Guidebook
              </div>
              <div style={{ ...base, fontSize: 10, fontWeight: 500, color: DS.textMuted, marginTop: 2 }}>
                Welcome · Wi-Fi · Local picks
              </div>
            </div>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={DS.textMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6, flexShrink: 0 }}>
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </div>

        {/* Paginated 2x2 grid */}
        <div style={{ flex: 1, position: 'relative', overflow: 'hidden', padding: `0 ${DS.spacing.lg}px` }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={safePage}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 8,
              }}
            >
              {services.slice(safePage * CARDS_PER_PAGE, (safePage + 1) * CARDS_PER_PAGE).map((svc) => (
                <ServiceCardMini
                  key={svc.id}
                  service={svc}
                  onTap={(s) => { onInteract(); onSelectService(s); }}
                />
              ))}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Pagination controls */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
          padding: `${DS.spacing.sm}px 0 ${DS.spacing.md}px`,
        }}>
          <button
            onClick={() => { onInteract(); setCardPage(Math.max(0, safePage - 1)); }}
            disabled={safePage === 0}
            aria-label="Previous services"
            style={{
              ...base,
              width: 28,
              height: 28,
              borderRadius: 14,
              border: `1px solid ${safePage === 0 ? DS.cardBorder : DS.textMuted}`,
              background: 'transparent',
              color: safePage === 0 ? DS.cardBorder : DS.textPrimary,
              fontSize: 14,
              cursor: safePage === 0 ? 'default' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1,
              opacity: safePage === 0 ? 0.4 : 1,
              transition: 'opacity 0.2s',
            }}
          >
            ‹
          </button>

          <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                onClick={() => { onInteract(); setCardPage(i); }}
                aria-label={`Page ${i + 1}`}
                style={{
                  width: safePage === i ? 16 : 6,
                  height: 6,
                  borderRadius: 3,
                  background: safePage === i ? DS.textPrimary : DS.cardBorder,
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                }}
              />
            ))}
          </div>

          <button
            onClick={() => { onInteract(); setCardPage(Math.min(totalPages - 1, safePage + 1)); }}
            disabled={safePage === totalPages - 1}
            aria-label="Next services"
            style={{
              ...base,
              width: 28,
              height: 28,
              borderRadius: 14,
              border: `1px solid ${safePage === totalPages - 1 ? DS.cardBorder : DS.textMuted}`,
              background: 'transparent',
              color: safePage === totalPages - 1 ? DS.cardBorder : DS.textPrimary,
              fontSize: 14,
              cursor: safePage === totalPages - 1 ? 'default' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1,
              opacity: safePage === totalPages - 1 ? 0.4 : 1,
              transition: 'opacity 0.2s',
            }}
          >
            ›
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Detail View ─── */
function DetailView({ service, onBack, onTryIt }) {
  return (
    <div style={{ ...base, width: PHONE_W, height: PHONE_H, background: DS.bg, display: 'flex', flexDirection: 'column' }}>
      <div style={{ position: 'relative', width: '100%', height: 190, flexShrink: 0, backgroundColor: '#2a2a2a' }}>
        <StatusBar light />
        <img
          src={service.image}
          alt=""
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, transparent 40%, rgba(0,0,0,0.5) 100%)',
          }}
        />
        <button
          onClick={onBack}
          aria-label="Go back"
          style={{
            ...base,
            position: 'absolute',
            top: 48,
            left: 16,
            width: 32,
            height: 32,
            borderRadius: 16,
            border: 'none',
            background: 'rgba(255,255,255,0.2)',
            backdropFilter: 'blur(8px)',
            color: '#fff',
            fontSize: 16,
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 1,
          }}
        >
          ←
        </button>
        <div style={{ position: 'absolute', bottom: 14, left: 18, right: 18 }}>
          <span
            style={{
              ...base,
              display: 'inline-block',
              padding: '2px 8px',
              borderRadius: 10,
              background: 'rgba(255,255,255,0.2)',
              color: '#fff',
              fontSize: 9,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 1,
              marginBottom: 4,
            }}
          >
            {CATEGORY_LABELS[service.category] || service.category}
          </span>
          <div style={{ ...base, fontSize: 22, fontWeight: 800, color: '#fff', letterSpacing: -0.5 }}>
            {service.name}
          </div>
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'hidden', padding: DS.spacing.lg }}>
        <p style={{ ...base, fontSize: 13, fontWeight: 400, lineHeight: 1.6, color: DS.textSecondary, margin: 0, marginBottom: DS.spacing.lg }}>
          {service.description}
        </p>

        <div
          style={{
            ...base,
            background: DS.card,
            borderRadius: DS.radius.xl,
            padding: DS.spacing.lg,
            marginBottom: DS.spacing.lg,
          }}
        >
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: DS.textMuted, marginBottom: DS.spacing.sm }}>
            Example Request
          </div>
          <div style={{ fontSize: 12, fontWeight: 400, color: DS.textSecondary, lineHeight: 1.5, fontStyle: 'italic' }}>
            "{service.exampleRequest}"
          </div>
        </div>

        <button
          onClick={onTryIt}
          aria-label={`Try ${service.name}`}
          style={{
            ...base,
            width: '100%',
            padding: '14px 0',
            borderRadius: DS.radius.md,
            border: 'none',
            backgroundColor: DS.btnPrimary,
            color: '#fff',
            fontSize: 15,
            fontWeight: 700,
            cursor: 'pointer',
            textAlign: 'center',
          }}
        >
          Try It
        </button>
      </div>
    </div>
  );
}

/* ─── Request View ─── */
function RequestView({ service, onBack, onSubmit }) {
  const [time, setTime] = useState('Afternoon');
  const [notes, setNotes] = useState(service.exampleRequest);
  const times = ['Morning', 'Afternoon', 'Evening'];

  return (
    <div style={{ ...base, width: PHONE_W, height: PHONE_H, background: DS.bg, display: 'flex', flexDirection: 'column' }}>
      <StatusBar />
      <div style={{ height: 44, flexShrink: 0 }} />
      <div style={{ display: 'flex', alignItems: 'center', padding: `0 ${DS.spacing.lg}px ${DS.spacing.md}px`, gap: 10 }}>
        <button
          onClick={onBack}
          aria-label="Go back"
          style={{
            ...base,
            width: 32,
            height: 32,
            borderRadius: 16,
            border: `1px solid ${DS.cardBorder}`,
            background: DS.bg,
            color: DS.textPrimary,
            fontSize: 16,
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 1,
            flexShrink: 0,
          }}
        >
          ←
        </button>
        <div style={{ ...base, fontSize: 16, fontWeight: 700, color: DS.textPrimary }}>
          Request {service.name}
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'hidden', padding: `0 ${DS.spacing.lg}px ${DS.spacing.lg}px` }}>
        <div style={{ marginBottom: DS.spacing.lg }}>
          <label style={{ ...base, display: 'block', fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: DS.textMuted, marginBottom: DS.spacing.sm }}>
            Preferred Date
          </label>
          <div
            style={{
              ...base,
              padding: '12px 14px',
              borderRadius: DS.radius.sm,
              border: `1px solid ${DS.cardBorder}`,
              backgroundColor: DS.card,
              fontSize: 14,
              color: DS.textPrimary,
            }}
          >
            Jun 16, 2026
          </div>
        </div>

        <div style={{ marginBottom: DS.spacing.lg }}>
          <label style={{ ...base, display: 'block', fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: DS.textMuted, marginBottom: DS.spacing.sm }}>
            Best Time
          </label>
          <div style={{ display: 'flex', gap: DS.spacing.sm }}>
            {times.map((t) => (
              <button
                key={t}
                onClick={() => setTime(t)}
                style={{
                  ...base,
                  flex: 1,
                  padding: '9px 0',
                  borderRadius: 20,
                  border: `1.5px solid ${t === time ? DS.btnPrimary : DS.cardBorder}`,
                  backgroundColor: t === time ? DS.btnPrimary : 'transparent',
                  color: t === time ? '#fff' : DS.textMuted,
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: 'pointer',
                  textAlign: 'center',
                }}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: DS.spacing.lg }}>
          <label style={{ ...base, display: 'block', fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: DS.textMuted, marginBottom: DS.spacing.sm }}>
            Notes
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            style={{
              ...base,
              width: '100%',
              padding: '12px 14px',
              borderRadius: DS.radius.sm,
              border: `1px solid ${DS.cardBorder}`,
              backgroundColor: DS.card,
              fontSize: 14,
              color: DS.textPrimary,
              resize: 'none',
              outline: 'none',
              lineHeight: 1.5,
            }}
          />
        </div>

        <button
          onClick={onSubmit}
          aria-label="Submit request"
          style={{
            ...base,
            width: '100%',
            padding: '14px 0',
            borderRadius: DS.radius.md,
            border: 'none',
            backgroundColor: DS.btnPrimary,
            color: '#fff',
            fontSize: 15,
            fontWeight: 700,
            cursor: 'pointer',
            textAlign: 'center',
          }}
        >
          Submit Request
        </button>
      </div>
    </div>
  );
}

/* ─── Confirm View ─── */
function ConfirmView({ service, onDone }) {
  return (
    <div
      style={{
        ...base,
        width: PHONE_W,
        height: PHONE_H,
        background: DS.bg,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: DS.spacing.xl,
        textAlign: 'center',
      }}
    >
      <StatusBar />
      <div
        style={{
          width: 64,
          height: 64,
          borderRadius: 32,
          background: DS.success,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: DS.spacing.lg,
          animation: 'phoneDemoCheckPop 0.4s cubic-bezier(0.34,1.56,0.64,1)',
        }}
      >
        <span style={{ color: '#fff', fontSize: 32, lineHeight: 1 }}>✓</span>
      </div>
      <div style={{ ...base, fontSize: 20, fontWeight: 700, color: DS.textPrimary, marginBottom: DS.spacing.sm }}>
        Request Confirmed
      </div>
      <div style={{ ...base, fontSize: 13, fontWeight: 400, color: DS.textSecondary, lineHeight: 1.5, marginBottom: DS.spacing.xl }}>
        Your <strong>{service.name}</strong> request has been received. Our concierge team will follow up with details.
      </div>

      <div
        style={{
          width: '100%',
          background: DS.card,
          borderRadius: DS.radius.xl,
          padding: DS.spacing.lg,
          marginBottom: DS.spacing.xl,
          textAlign: 'left',
        }}
      >
        <div style={{ ...base, fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: DS.textMuted, marginBottom: DS.spacing.sm }}>
          Summary
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
          <span style={{ ...base, fontSize: 12, color: DS.textSecondary }}>Service</span>
          <span style={{ ...base, fontSize: 12, fontWeight: 600, color: DS.textPrimary }}>{service.name}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
          <span style={{ ...base, fontSize: 12, color: DS.textSecondary }}>Date</span>
          <span style={{ ...base, fontSize: 12, fontWeight: 600, color: DS.textPrimary }}>Jun 16, 2026</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ ...base, fontSize: 12, color: DS.textSecondary }}>Time</span>
          <span style={{ ...base, fontSize: 12, fontWeight: 600, color: DS.textPrimary }}>Afternoon</span>
        </div>
      </div>

      <button
        onClick={onDone}
        aria-label="Back to services"
        style={{
          ...base,
          width: '100%',
          padding: '14px 0',
          borderRadius: DS.radius.md,
          border: `1px solid ${DS.cardBorder}`,
          backgroundColor: 'transparent',
          color: DS.textSecondary,
          fontSize: 14,
          fontWeight: 600,
          cursor: 'pointer',
          textAlign: 'center',
        }}
      >
        Back to Services
      </button>
    </div>
  );
}

/* ─── Grocery Shop View ─── */
function GroceryShopView({ onBack }) {
  const [activeCategory, setActiveCategory] = useState('All');
  const [cart, setCart] = useState({});
  const [brokenImages, setBrokenImages] = useState(new Set());
  const cartCount = Object.values(cart).reduce((sum, qty) => sum + qty, 0);

  const handleImageError = useCallback((productId) => {
    setBrokenImages((prev) => {
      if (prev.has(productId)) return prev;
      const next = new Set(prev);
      next.add(productId);
      return next;
    });
  }, []);

  const filtered = (activeCategory === 'All'
    ? MOCK_PRODUCTS
    : MOCK_PRODUCTS.filter((p) => p.category === activeCategory)
  ).filter((p) => !brokenImages.has(p.id));

  const addToCart = (productId, e) => {
    e.stopPropagation();
    setCart((prev) => ({ ...prev, [productId]: (prev[productId] || 0) + 1 }));
  };

  const removeFromCart = (productId, e) => {
    e.stopPropagation();
    setCart((prev) => {
      const next = { ...prev };
      if (next[productId] > 1) { next[productId] -= 1; } else { delete next[productId]; }
      return next;
    });
  };

  return (
    <div style={{ ...base, width: PHONE_W, height: PHONE_H, background: DS.bg, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <StatusBar />
      <div style={{ height: 44, flexShrink: 0 }} />

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', padding: `0 ${DS.spacing.lg}px ${DS.spacing.sm}px`, gap: 10 }}>
        <button
          onClick={onBack}
          aria-label="Go back"
          style={{
            ...base,
            width: 30,
            height: 30,
            borderRadius: 15,
            border: `1px solid ${DS.cardBorder}`,
            background: DS.bg,
            color: DS.textPrimary,
            fontSize: 15,
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 1,
            flexShrink: 0,
          }}
        >
          ←
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ ...base, fontSize: 16, fontWeight: 700, color: DS.textPrimary, lineHeight: 1.2 }}>
            Shop Groceries
          </div>
          <div style={{ ...base, fontSize: 10, fontWeight: 500, color: DS.textMuted, marginTop: 2 }}>
            20,000+ products
          </div>
        </div>
      </div>

      {/* Category pills */}
      <div className="phoneDemoGroceryScroll" style={{
        display: 'flex',
        gap: 6,
        padding: `${DS.spacing.xs}px ${DS.spacing.lg}px ${DS.spacing.sm}px`,
        overflowX: 'auto',
        flexShrink: 0,
        WebkitOverflowScrolling: 'touch',
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
      }}>
        {GROCERY_CATEGORIES.map((cat) => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            style={{
              ...base,
              whiteSpace: 'nowrap',
              padding: '5px 11px',
              borderRadius: 20,
              border: activeCategory === cat ? 'none' : `1px solid ${DS.cardBorder}`,
              background: activeCategory === cat ? DS.btnPrimary : DS.card,
              color: activeCategory === cat ? '#fff' : DS.textSecondary,
              fontSize: 10,
              fontWeight: 600,
              cursor: 'pointer',
              flexShrink: 0,
              transition: 'all 0.2s ease',
            }}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Product grid */}
      <div className="phoneDemoGroceryScroll" style={{
        flex: 1,
        overflowY: 'auto',
        padding: `0 ${DS.spacing.lg}px ${DS.spacing.lg + (cartCount > 0 ? 44 : 0)}px`,
        WebkitOverflowScrolling: 'touch',
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 8,
        }}>
          {filtered.map((product) => {
            const qty = cart[product.id] || 0;
            const [intPart, decRaw = '00'] = product.price.split('.');
            const decPart = (decRaw + '00').slice(0, 2);
            return (
              <div
                key={product.id}
                style={{
                  ...base,
                  background: DS.card,
                  borderRadius: DS.radius.md,
                  border: `1px solid ${DS.cardBorder}`,
                  padding: 8,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  position: 'relative',
                  transition: 'transform 0.15s ease',
                }}
              >
                <div style={{
                  width: 56,
                  height: 56,
                  borderRadius: 8,
                  overflow: 'hidden',
                  background: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 6,
                  flexShrink: 0,
                }}>
                  <img
                    src={product.image}
                    alt=""
                    loading="lazy"
                    onError={() => handleImageError(product.id)}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
                <div style={{
                  ...base,
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 1,
                  marginBottom: 2,
                }}>
                  <span style={{ fontSize: 8, color: DS.textMuted }}>$</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: DS.textPrimary, lineHeight: 1 }}>{intPart}</span>
                  <span style={{ fontSize: 9, fontWeight: 600, color: DS.textPrimary }}>.{decPart}</span>
                </div>
                <div style={{
                  ...base,
                  fontSize: 10,
                  fontWeight: 500,
                  color: DS.textPrimary,
                  textAlign: 'center',
                  lineHeight: 1.25,
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  marginBottom: 2,
                  width: '100%',
                }}>
                  {product.name}
                </div>
                <div style={{ ...base, fontSize: 9, color: DS.textMuted, marginBottom: 6 }}>
                  {product.size}
                </div>

                {qty === 0 ? (
                  <button
                    onClick={(e) => addToCart(product.id, e)}
                    aria-label={`Add ${product.name}`}
                    style={{
                      ...base,
                      width: 26,
                      height: 26,
                      borderRadius: 13,
                      border: 'none',
                      background: DS.btnPrimary,
                      color: '#fff',
                      fontSize: 16,
                      fontWeight: 400,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      lineHeight: 1,
                      transition: 'transform 0.15s ease',
                    }}
                  >
                    +
                  </button>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <button
                      onClick={(e) => removeFromCart(product.id, e)}
                      aria-label="Decrease quantity"
                      style={{
                        ...base,
                        width: 22,
                        height: 22,
                        borderRadius: 11,
                        border: `1px solid ${DS.cardBorder}`,
                        background: DS.bg,
                        color: DS.textPrimary,
                        fontSize: 14,
                        fontWeight: 500,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: 1,
                      }}
                    >
                      −
                    </button>
                    <span style={{ ...base, fontSize: 12, fontWeight: 700, color: DS.textPrimary, minWidth: 12, textAlign: 'center' }}>
                      {qty}
                    </span>
                    <button
                      onClick={(e) => addToCart(product.id, e)}
                      aria-label="Increase quantity"
                      style={{
                        ...base,
                        width: 22,
                        height: 22,
                        borderRadius: 11,
                        border: 'none',
                        background: DS.btnPrimary,
                        color: '#fff',
                        fontSize: 14,
                        fontWeight: 400,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: 1,
                      }}
                    >
                      +
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Cart badge */}
      {cartCount > 0 && (
        <div style={{
          position: 'absolute',
          bottom: 18,
          left: '50%',
          transform: 'translateX(-50%)',
          background: DS.btnPrimary,
          color: '#fff',
          borderRadius: 20,
          padding: '7px 18px',
          fontSize: 11,
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          zIndex: 20,
          boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
          animation: 'phoneDemoCardEnter 0.25s ease-out',
          whiteSpace: 'nowrap',
          letterSpacing: 0.2,
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
            <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6" />
          </svg>
          {cartCount} {cartCount === 1 ? 'item' : 'items'}
        </div>
      )}
    </div>
  );
}

/* ─── Guidebook View ─── */
const GUIDEBOOK_TABS = [
  { id: 'welcome', label: 'Welcome' },
  { id: 'access', label: 'Wi-Fi & Access' },
  { id: 'rules', label: 'House Rules' },
  { id: 'local', label: 'Local Picks' },
];

const GUIDEBOOK_LOCAL = [
  { name: 'Coastal Table', cat: 'Seafood · Date night', dist: '8 min', img: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=240&h=240&fit=crop&q=80' },
  { name: 'Morning Tide', cat: 'Coffee · Walkable', dist: '5 min', img: 'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=240&h=240&fit=crop&q=80' },
  { name: 'Sunset Point Trail', cat: 'Outdoors · Scenic', dist: '12 min', img: 'https://images.unsplash.com/photo-1501555088652-021faa106b9b?w=240&h=240&fit=crop&q=80' },
];

const GUIDEBOOK_ACCESS = [
  { label: 'Wi-Fi Network', value: 'Bellhopt Guest' },
  { label: 'Password', value: 'welcome2026' },
  { label: 'Front Door', value: 'Keypad · 4-8-2-1' },
  { label: 'Check-in', value: 'After 4:00 PM' },
  { label: 'Check-out', value: 'By 10:00 AM' },
];

const GUIDEBOOK_RULES = [
  'No smoking anywhere on the property',
  'Quiet hours after 10:00 PM',
  'Pets welcome with prior approval',
  'Please reset furniture before checkout',
];

function GuidebookView({ onBack, hostLogo }) {
  const [tab, setTab] = useState('welcome');

  return (
    <div style={{ ...base, width: PHONE_W, height: PHONE_H, background: DS.bg, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Hero */}
      <div style={{ position: 'relative', width: '100%', height: 170, flexShrink: 0, backgroundColor: '#2a2a2a' }}>
        <StatusBar light />
        <img src={UNSPLASH_GUIDEBOOK_COVER} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.25) 0%, transparent 40%, rgba(0,0,0,0.65) 100%)' }} />
        <button
          onClick={onBack}
          aria-label="Go back"
          style={{
            ...base,
            position: 'absolute',
            top: 48,
            left: 16,
            width: 32,
            height: 32,
            borderRadius: 16,
            border: 'none',
            background: 'rgba(255,255,255,0.22)',
            backdropFilter: 'blur(8px)',
            color: '#fff',
            fontSize: 16,
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 1,
          }}
        >
          ←
        </button>
        <div style={{ position: 'absolute', bottom: 12, left: 18, right: 18 }}>
          <div style={{ ...base, fontSize: 9, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: 'rgba(255,255,255,0.75)', marginBottom: 4 }}>
            {hostLogo ? 'Your Stay' : 'Bellhopt Guidebook'}
          </div>
          <div style={{ ...base, fontSize: 20, fontWeight: 800, color: '#fff', letterSpacing: -0.4, lineHeight: 1.15 }}>
            The Coastal House
          </div>
          <div style={{ ...base, fontSize: 11, fontWeight: 500, color: 'rgba(255,255,255,0.8)', marginTop: 2 }}>
            Jun 15 – 20 · 5 nights
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="phoneDemoGroceryScroll" style={{
        display: 'flex',
        gap: 6,
        padding: `${DS.spacing.sm}px ${DS.spacing.lg}px`,
        overflowX: 'auto',
        flexShrink: 0,
        WebkitOverflowScrolling: 'touch',
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
        borderBottom: `1px solid ${DS.divider}`,
      }}>
        {GUIDEBOOK_TABS.map((t) => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                ...base,
                whiteSpace: 'nowrap',
                padding: '5px 11px',
                borderRadius: 20,
                border: active ? 'none' : `1px solid ${DS.cardBorder}`,
                background: active ? DS.btnPrimary : DS.bg,
                color: active ? '#fff' : DS.textSecondary,
                fontSize: 10,
                fontWeight: 600,
                cursor: 'pointer',
                flexShrink: 0,
                transition: 'all 0.2s ease',
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div className="phoneDemoGroceryScroll" style={{ flex: 1, overflowY: 'auto', padding: `${DS.spacing.md}px ${DS.spacing.lg}px ${DS.spacing.lg}px`, WebkitOverflowScrolling: 'touch', msOverflowStyle: 'none', scrollbarWidth: 'none' }}>
        {tab === 'welcome' && (
          <div>
            <div style={{ ...base, fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: DS.textMuted, marginBottom: 6 }}>
              From your host
            </div>
            <div style={{ ...base, fontSize: 13, fontWeight: 400, lineHeight: 1.55, color: DS.textSecondary, marginBottom: DS.spacing.md }}>
              Welcome to The Coastal House. We've stocked a few essentials for your arrival and put together our favorite corners of the neighborhood. Tap the concierge anytime — we're happy to help.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div style={{ background: DS.card, borderRadius: DS.radius.md, padding: 10 }}>
                <div style={{ ...base, fontSize: 9, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: DS.textMuted, marginBottom: 4 }}>Check-in</div>
                <div style={{ ...base, fontSize: 13, fontWeight: 700, color: DS.textPrimary }}>4:00 PM</div>
              </div>
              <div style={{ background: DS.card, borderRadius: DS.radius.md, padding: 10 }}>
                <div style={{ ...base, fontSize: 9, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: DS.textMuted, marginBottom: 4 }}>Check-out</div>
                <div style={{ ...base, fontSize: 13, fontWeight: 700, color: DS.textPrimary }}>10:00 AM</div>
              </div>
            </div>
          </div>
        )}

        {tab === 'access' && (
          <div>
            <div style={{ ...base, fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: DS.textMuted, marginBottom: 8 }}>
              Access details
            </div>
            <div style={{ background: DS.card, borderRadius: DS.radius.md, overflow: 'hidden' }}>
              {GUIDEBOOK_ACCESS.map((row, idx) => (
                <div
                  key={row.label}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '10px 12px',
                    borderTop: idx === 0 ? 'none' : `1px solid ${DS.divider}`,
                  }}
                >
                  <span style={{ ...base, fontSize: 11, color: DS.textSecondary }}>{row.label}</span>
                  <span style={{ ...base, fontSize: 11, fontWeight: 700, color: DS.textPrimary }}>{row.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === 'rules' && (
          <div>
            <div style={{ ...base, fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: DS.textMuted, marginBottom: 8 }}>
              Please respect
            </div>
            {GUIDEBOOK_RULES.map((rule, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  padding: '8px 0',
                  borderBottom: idx === GUIDEBOOK_RULES.length - 1 ? 'none' : `1px solid ${DS.divider}`,
                }}
              >
                <span
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: 8,
                    background: DS.btnPrimary,
                    color: '#fff',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 9,
                    fontWeight: 800,
                    flexShrink: 0,
                    marginTop: 2,
                  }}
                >
                  ✓
                </span>
                <span style={{ ...base, fontSize: 12, color: DS.textSecondary, lineHeight: 1.5 }}>{rule}</span>
              </div>
            ))}
          </div>
        )}

        {tab === 'local' && (
          <div>
            <div style={{ ...base, fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: DS.textMuted, marginBottom: 8 }}>
              Handpicked near you
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {GUIDEBOOK_LOCAL.map((spot) => (
                <div
                  key={spot.name}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: 8,
                    borderRadius: DS.radius.md,
                    background: DS.card,
                    border: `1px solid ${DS.cardBorder}`,
                  }}
                >
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 10,
                      overflow: 'hidden',
                      flexShrink: 0,
                      backgroundImage: `url(${spot.img})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ ...base, fontSize: 12, fontWeight: 700, color: DS.textPrimary, lineHeight: 1.2 }}>{spot.name}</div>
                    <div style={{ ...base, fontSize: 10, fontWeight: 500, color: DS.textMuted, marginTop: 2 }}>{spot.cat}</div>
                  </div>
                  <span
                    style={{
                      ...base,
                      fontSize: 9,
                      fontWeight: 700,
                      color: DS.textPrimary,
                      background: DS.bg,
                      border: `1px solid ${DS.cardBorder}`,
                      padding: '3px 7px',
                      borderRadius: 10,
                      flexShrink: 0,
                    }}
                  >
                    {spot.dist}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Chat View ─── */
const CHAT_MESSAGES = [
  { from: 'ai', text: 'Hi Guest — I\'m your AI concierge. How can I make your stay better today?' },
  { from: 'user', text: 'Best sushi nearby for Saturday?' },
  {
    from: 'ai',
    text: 'Three favorites within 10 minutes:',
    chips: [
      { label: 'Koji Omakase', sub: 'Chef\'s table · 0.9 mi' },
      { label: 'Blue Wave Sushi', sub: 'Family · 1.2 mi' },
      { label: 'Hana Bar', sub: 'Late-night · 1.5 mi' },
    ],
  },
  { from: 'user', text: 'Book Koji for 2 at 8 PM' },
  { from: 'ai', text: 'Done — Koji Omakase, Sat 8:00 PM for 2. I\'ll text a reminder at 6.' },
];

function ChatView({ onBack }) {
  return (
    <div style={{ ...base, width: PHONE_W, height: PHONE_H, background: DS.bg, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <StatusBar />
      <div style={{ height: 44, flexShrink: 0 }} />

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', padding: `0 ${DS.spacing.lg}px ${DS.spacing.sm}px`, gap: 10 }}>
        <button
          onClick={onBack}
          aria-label="Go back"
          style={{
            ...base,
            width: 30,
            height: 30,
            borderRadius: 15,
            border: `1px solid ${DS.cardBorder}`,
            background: DS.bg,
            color: DS.textPrimary,
            fontSize: 15,
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 1,
            flexShrink: 0,
          }}
        >
          ←
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ ...base, fontSize: 14, fontWeight: 700, color: DS.textPrimary, lineHeight: 1.2 }}>
            AI Concierge
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 2 }}>
            <span style={{ width: 6, height: 6, borderRadius: 3, background: DS.success }} />
            <span style={{ ...base, fontSize: 10, fontWeight: 500, color: DS.textMuted }}>
              Online · answers in seconds
            </span>
          </div>
        </div>
      </div>

      <div style={{ height: 1, background: DS.divider, flexShrink: 0 }} />

      {/* Messages */}
      <div className="phoneDemoGroceryScroll" style={{ flex: 1, overflowY: 'auto', padding: `${DS.spacing.md}px ${DS.spacing.lg}px`, display: 'flex', flexDirection: 'column', gap: 8, WebkitOverflowScrolling: 'touch', msOverflowStyle: 'none', scrollbarWidth: 'none' }}>
        {CHAT_MESSAGES.map((msg, idx) => {
          const mine = msg.from === 'user';
          return (
            <div
              key={idx}
              style={{
                display: 'flex',
                justifyContent: mine ? 'flex-end' : 'flex-start',
                width: '100%',
              }}
            >
              <div style={{ maxWidth: '82%', display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div
                  style={{
                    ...base,
                    fontSize: 12,
                    fontWeight: 500,
                    lineHeight: 1.45,
                    padding: '8px 12px',
                    borderRadius: 14,
                    background: mine ? DS.btnPrimary : DS.card,
                    color: mine ? '#fff' : DS.textPrimary,
                    border: mine ? 'none' : `1px solid ${DS.cardBorder}`,
                    borderBottomRightRadius: mine ? 4 : 14,
                    borderBottomLeftRadius: mine ? 14 : 4,
                  }}
                >
                  {msg.text}
                </div>
                {msg.chips && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    {msg.chips.map((chip) => (
                      <div
                        key={chip.label}
                        style={{
                          ...base,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '7px 10px',
                          borderRadius: 12,
                          background: DS.bg,
                          border: `1px solid ${DS.cardBorder}`,
                        }}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span style={{ ...base, fontSize: 11, fontWeight: 700, color: DS.textPrimary, lineHeight: 1.2 }}>{chip.label}</span>
                          <span style={{ ...base, fontSize: 9, fontWeight: 500, color: DS.textMuted, marginTop: 2 }}>{chip.sub}</span>
                        </div>
                        <span
                          style={{
                            ...base,
                            fontSize: 9,
                            fontWeight: 700,
                            color: DS.btnPrimary,
                            textTransform: 'uppercase',
                            letterSpacing: 1,
                          }}
                        >
                          Book
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Composer */}
      <div style={{ padding: `${DS.spacing.sm}px ${DS.spacing.lg}px ${DS.spacing.md}px`, borderTop: `1px solid ${DS.divider}`, background: DS.bg, flexShrink: 0 }}>
        <div
          style={{
            ...base,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            height: 36,
            borderRadius: 18,
            border: `1px solid ${DS.cardBorder}`,
            background: DS.card,
            padding: '0 6px 0 12px',
          }}
        >
          <span style={{ flex: 1, fontSize: 11, color: DS.textMuted }}>Ask anything…</span>
          <span
            style={{
              width: 26,
              height: 26,
              borderRadius: 13,
              background: DS.btnPrimary,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 14,
              fontWeight: 700,
            }}
          >
            ↑
          </span>
        </div>
      </div>
    </div>
  );
}

/* ─── Main PhoneDemo ─── */
export default function PhoneDemo({ enabledServices, hostLogo }) {
  const visibleServices = useMemo(() => {
    if (!enabledServices) return DEMO_SERVICES;
    return DEMO_SERVICES.filter(
      (s) => s.tier === 'core' || enabledServices.has(s.id)
    );
  }, [enabledServices]);

  const totalPages = Math.max(1, Math.ceil(visibleServices.length / CARDS_PER_PAGE));

  const [view, setView] = useState(VIEWS.HOME);
  const [activeService, setActiveService] = useState(null);
  const [cardPage, setCardPage] = useState(0);
  const [showHint, setShowHint] = useState(true);

  const autoTimerRef = useRef(null);
  const pauseTimeoutRef = useRef(null);
  const isPausedRef = useRef(false);

  useEffect(() => {
    setCardPage((prev) => Math.min(prev, totalPages - 1));
  }, [totalPages]);

  const startAutoPlay = useCallback(() => {
    if (autoTimerRef.current) clearInterval(autoTimerRef.current);
    autoTimerRef.current = setInterval(() => {
      if (!isPausedRef.current) {
        setCardPage((prev) => (prev + 1) % totalPages);
      }
    }, AUTO_ADVANCE_MS);
  }, [totalPages]);

  const handleInteract = useCallback(() => {
    setShowHint(false);
    isPausedRef.current = true;
    if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current);
    pauseTimeoutRef.current = setTimeout(() => {
      isPausedRef.current = false;
    }, PAUSE_AFTER_INTERACT_MS);
  }, []);

  useEffect(() => {
    if (view === VIEWS.HOME) {
      startAutoPlay();
    } else {
      if (autoTimerRef.current) clearInterval(autoTimerRef.current);
    }
    return () => {
      if (autoTimerRef.current) clearInterval(autoTimerRef.current);
    };
  }, [view, startAutoPlay]);

  useEffect(() => {
    const hintTimer = setTimeout(() => setShowHint(false), 3000);
    return () => clearTimeout(hintTimer);
  }, []);

  useEffect(() => {
    return () => {
      if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current);
    };
  }, []);

  const goHome = useCallback(() => {
    setView(VIEWS.HOME);
    setActiveService(null);
  }, []);

  const selectService = useCallback((svc) => {
    setActiveService(svc);
    setView(svc.id === 'grocery-delivery' ? VIEWS.GROCERY : VIEWS.DETAIL);
  }, []);

  const goRequest = useCallback(() => setView(VIEWS.REQUEST), []);
  const goConfirm = useCallback(() => setView(VIEWS.CONFIRM), []);
  const openGuidebook = useCallback(() => setView(VIEWS.GUIDEBOOK), []);
  const openChat = useCallback(() => setView(VIEWS.CHAT), []);

  return (
    <div
      style={{
        width: PHONE_W,
        height: PHONE_H,
        overflow: 'hidden',
        borderRadius: 0,
        background: DS.bg,
        position: 'relative',
        userSelect: 'none',
      }}
    >
      <style>{`
        @keyframes phoneDemoCheckPop {
          0% { transform: scale(0); opacity: 0; }
          60% { transform: scale(1.15); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes phoneDemoCardEnter {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes phoneDemoHintPulse {
          0%, 100% { opacity: 0.9; transform: translateX(-50%) scale(1); }
          50%      { opacity: 1;   transform: translateX(-50%) scale(1.04); }
        }
        .phoneDemoGroceryScroll::-webkit-scrollbar { display: none; }
      `}</style>

      {/* View layers */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          transition: 'transform 0.3s ease, opacity 0.3s ease',
          transform: view === VIEWS.HOME ? 'translateX(0)' : 'translateX(-100%)',
          opacity: view === VIEWS.HOME ? 1 : 0,
          pointerEvents: view === VIEWS.HOME ? 'auto' : 'none',
        }}
      >
        <HomeView
          services={visibleServices}
          onSelectService={selectService}
          cardPage={cardPage}
          setCardPage={setCardPage}
          onInteract={handleInteract}
          hostLogo={hostLogo}
          onOpenGuidebook={openGuidebook}
          onOpenChat={openChat}
        />
      </div>

      <div
        style={{
          position: 'absolute',
          inset: 0,
          transition: 'transform 0.3s ease, opacity 0.3s ease',
          transform: view === VIEWS.DETAIL ? 'translateX(0)' : 'translateX(100%)',
          opacity: view === VIEWS.DETAIL ? 1 : 0,
          pointerEvents: view === VIEWS.DETAIL ? 'auto' : 'none',
        }}
      >
        {activeService && <DetailView service={activeService} onBack={goHome} onTryIt={goRequest} />}
      </div>

      <div
        style={{
          position: 'absolute',
          inset: 0,
          transition: 'transform 0.3s ease, opacity 0.3s ease',
          transform: view === VIEWS.GROCERY ? 'translateX(0)' : 'translateX(100%)',
          opacity: view === VIEWS.GROCERY ? 1 : 0,
          pointerEvents: view === VIEWS.GROCERY ? 'auto' : 'none',
        }}
      >
        <GroceryShopView onBack={goHome} />
      </div>

      <div
        style={{
          position: 'absolute',
          inset: 0,
          transition: 'transform 0.3s ease, opacity 0.3s ease',
          transform: view === VIEWS.REQUEST ? 'translateX(0)' : 'translateX(100%)',
          opacity: view === VIEWS.REQUEST ? 1 : 0,
          pointerEvents: view === VIEWS.REQUEST ? 'auto' : 'none',
        }}
      >
        {activeService && (
          <RequestView
            service={activeService}
            onBack={() => setView(VIEWS.DETAIL)}
            onSubmit={goConfirm}
          />
        )}
      </div>

      <div
        style={{
          position: 'absolute',
          inset: 0,
          transition: 'transform 0.3s ease, opacity 0.3s ease',
          transform: view === VIEWS.CONFIRM ? 'translateX(0)' : 'translateX(100%)',
          opacity: view === VIEWS.CONFIRM ? 1 : 0,
          pointerEvents: view === VIEWS.CONFIRM ? 'auto' : 'none',
        }}
      >
        {activeService && <ConfirmView service={activeService} onDone={goHome} />}
      </div>

      <div
        style={{
          position: 'absolute',
          inset: 0,
          transition: 'transform 0.3s ease, opacity 0.3s ease',
          transform: view === VIEWS.GUIDEBOOK ? 'translateX(0)' : 'translateX(100%)',
          opacity: view === VIEWS.GUIDEBOOK ? 1 : 0,
          pointerEvents: view === VIEWS.GUIDEBOOK ? 'auto' : 'none',
        }}
      >
        <GuidebookView onBack={goHome} hostLogo={hostLogo} />
      </div>

      <div
        style={{
          position: 'absolute',
          inset: 0,
          transition: 'transform 0.3s ease, opacity 0.3s ease',
          transform: view === VIEWS.CHAT ? 'translateX(0)' : 'translateX(100%)',
          opacity: view === VIEWS.CHAT ? 1 : 0,
          pointerEvents: view === VIEWS.CHAT ? 'auto' : 'none',
        }}
      >
        <ChatView onBack={goHome} />
      </div>

      {/* Tap to explore hint */}
      {showHint && view === VIEWS.HOME && (
        <div
          style={{
            position: 'absolute',
            bottom: 60,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(17,17,17,0.8)',
            color: '#fff',
            fontSize: 11,
            fontWeight: 500,
            padding: '5px 14px',
            borderRadius: 20,
            pointerEvents: 'none',
            zIndex: 20,
            whiteSpace: 'nowrap',
            animation: 'phoneDemoHintPulse 2s ease-in-out infinite',
            letterSpacing: 0.2,
          }}
        >
          Tap a service to explore
        </div>
      )}

      {/* Home indicator bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 6,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 120,
          height: 4,
          borderRadius: 2,
          background: 'rgba(0,0,0,0.15)',
          zIndex: 15,
          pointerEvents: 'none',
        }}
      />
    </div>
  );
}

PhoneDemo.WIDTH = PHONE_W;
PhoneDemo.HEIGHT = PHONE_H;
